import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// React Router does not move the scroll position when the route changes. A
// browser does this for a real page load, so nothing in the app ever asked for
// it -- which meant tapping a link two thirds of the way down the landing page
// dropped you two thirds of the way down the next one. It is worst on a phone,
// where the pages are longest and there is no visible page frame to tell you
// that you are looking at the middle of something new.
//
// Three cases, and they are different:
//
//   POP        back and forward. The browser already remembers where you were,
//              and overriding that is the thing people find most annoying about
//              single-page apps. Left alone deliberately.
//   a #hash    scroll to that element, not to the top -- otherwise an anchor
//              link silently stops working.
//   otherwise  top of the page.
//
// useLayoutEffect rather than useEffect: the scroll happens before the browser
// paints, so there is no frame where the new page is rendered at the old
// offset. With useEffect that frame is visible as a jump.

export default function ScrollToTop() {
    const { pathname, hash } = useLocation();
    const navigationType = useNavigationType();

    useLayoutEffect(() => {
        if (navigationType === "POP") return;

        if (hash) {
            // The target may not exist yet if the page loads its own data, in
            // which case falling through to the top is the better failure.
            const target = document.getElementById(hash.slice(1));
            if (target) {
                target.scrollIntoView();
                return;
            }
        }

        window.scrollTo(0, 0);
    }, [pathname, hash, navigationType]);

    return null;
}
